import { ROUND_OFF_LABEL, type TravelBookingAdminRefundQuote } from '@utazzo/shared';
import { UzAlert, UzButton, UzDialog, UzNumberInput, UzSkeleton, UzTextarea } from '@utazzo/ui';
import { Undo2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { money, signedMoney } from '../booking-format';
import { readRefusal } from '../travel-bookings.api';
import { useRefundQuote, useRefundTravelBooking } from '../use-travel-bookings';

import { KeyValueList } from './KeyValueList';

/**
 * "Refund" on any booking that holds guest money.
 *
 * Lays the figures out and lets the operator decide. First: what the guest
 * paid and what it was made of. Then: what the supplier did with it - its
 * net, its cancellation charge and credit when a cancellation has settled -
 * and what has already gone back. Below that, one number the operator
 * types, bounded by what is still refundable and pre-filled only where the
 * answer is obvious (a hold that never booked anything).
 *
 * Confirming sends the refund to the gateway straight away. On a held
 * booking it also closes the hold and emails the guest; the dialog says so
 * before the button is pressed.
 *
 * Read on phones as often as on desks: the two figure blocks stack below
 * `md`, the amount and note stack below `sm`, and the footer buttons go
 * full width so a thumb cannot miss.
 */
export const RefundDialog = ({
  open,
  onOpenChange,
  bookingId,
  reference,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string;
  reference: string;
  onDone: () => void;
}): React.JSX.Element => {
  const quote = useRefundQuote(open ? bookingId : null);
  const refund = useRefundTravelBooking();
  const [rupees, setRupees] = useState<number | ''>('');
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);

  // Fresh figures every time the dialog opens; the amount follows the
  // suggestion until the operator edits it.
  useEffect(() => {
    if (!open) return;
    setNote('');
    setTouched(false);
    setRupees('');
  }, [open]);
  useEffect(() => {
    if (touched || !quote.data) return;
    const suggested = quote.data.suggestedRefundPaise;
    setRupees(suggested === null ? '' : Number(BigInt(suggested)) / 100);
  }, [quote.data, touched]);

  const q = quote.data;
  const c = q?.currencyCode ?? 'INR';
  const maxPaise = q ? BigInt(q.maxRefundPaise) : 0n;
  const amountPaise = useMemo(
    () => (rupees === '' || !Number.isFinite(rupees) ? null : BigInt(Math.round(rupees * 100))),
    [rupees],
  );
  const amountError =
    amountPaise === null
      ? 'Enter the amount to refund.'
      : amountPaise <= 0n
        ? 'The amount must be more than zero.'
        : amountPaise > maxPaise
          ? `At most ${money(maxPaise, c)} can still be refunded.`
          : undefined;
  const canConfirm = Boolean(q?.refundable) && !amountError && !refund.isPending;
  const onHold = q?.status === 'ON_HOLD';

  const confirm = (): void => {
    if (!q || amountPaise === null || amountError) return;
    refund.mutate(
      {
        id: bookingId,
        input: {
          amountPaise: amountPaise.toString(),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          onDone();
        },
        onError: (e) => {
          const r = readRefusal(e);
          toast.error(r?.message ?? 'The refund could not be sent. Try again in a moment.', {
            duration: 10_000,
          });
          // Someone else got there first, or the figures moved: re-read them.
          if (
            r?.code === 'NOT_ON_HOLD' ||
            r?.code === 'NOT_REFUNDABLE' ||
            r?.code === 'BAD_AMOUNT'
          ) {
            void quote.refetch();
            if (r.code === 'NOT_ON_HOLD') {
              onOpenChange(false);
              onDone();
            }
          }
        },
      },
    );
  };

  return (
    <UzDialog
      open={open}
      onOpenChange={(next) => {
        if (refund.isPending) return;
        onOpenChange(next);
      }}
      title={`Refund - ${reference}`}
      description={
        onHold
          ? 'Ends the hold without booking, emails the guest, and sends the refund to the gateway now. This cannot be undone.'
          : 'Sends the refund to the gateway now. This cannot be undone.'
      }
      size="xl"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <UzButton
            variant="ghost"
            className="w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
            disabled={refund.isPending}
          >
            {onHold ? 'Keep on hold' : 'Cancel'}
          </UzButton>
          <UzButton
            variant="destructive"
            className="w-full sm:w-auto"
            onClick={confirm}
            disabled={!canConfirm}
            loading={refund.isPending}
            leftIcon={<Undo2 className="h-4 w-4" />}
          >
            {amountPaise && !amountError
              ? `Confirm refund of ${money(amountPaise, c)}`
              : 'Confirm refund'}
          </UzButton>
        </div>
      }
    >
      {quote.isLoading || !q ? (
        <div className="flex flex-col gap-3">
          <UzSkeleton className="h-24 w-full" />
          <UzSkeleton className="h-24 w-full" />
          {quote.isError && (
            <UzAlert
              tone="destructive"
              title="Could not load the figures"
              description="Close and try again. Nothing has been refunded."
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {!q.refundable && (
            <UzAlert
              tone="destructive"
              title="Nothing can be refunded"
              description={q.blockers.join(' ')}
            />
          )}
          {q.isPartlyIssued && (
            <UzAlert
              tone="warning"
              title="A ticket exists on this booking"
              description="At least one leg was ticketed before the booking failed. Refund only what the guest is actually owed."
            />
          )}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <KeyValueList
              title="What the guest paid"
              items={[
                { term: 'Guest', value: q.guestName || '-' },
                { term: 'Total paid', value: money(q.guestPaidPaise, c), strong: true },
                { term: 'Base fare / rate', value: money(q.basePaise, c) },
                { term: 'Taxes', value: money(q.taxPaise, c) },
                ...(BigInt(q.ssrPaise) > 0n
                  ? [{ term: 'Extras', value: money(q.ssrPaise, c) }]
                  : []),
                { term: 'Markup', value: money(q.markupPaise, c) },
                { term: 'Convenience fee', value: money(q.convenienceFeePaise, c) },
                { term: 'GST', value: money(q.gstPaise, c) },
                ...(BigInt(q.tcsPaise) > 0n ? [{ term: 'TCS', value: money(q.tcsPaise, c) }] : []),
                ...(q.roundOffPaise !== '0'
                  ? [{ term: ROUND_OFF_LABEL, value: signedMoney(q.roundOffPaise, c) }]
                  : []),
              ]}
            />
            <KeyValueList
              title="Supplier and refunds so far"
              items={[
                {
                  term: 'Supplier net',
                  value: `${money(q.supplierNetMinor, q.supplierCurrency)} ${q.supplierCurrency}`,
                },
                { term: 'Supplier charge', value: supplierFigure(q, q.supplierChargePaise, c) },
                {
                  term: 'Supplier credits us',
                  value: supplierFigure(q, q.supplierCreditPaise, c),
                },
                { term: 'Already refunded', value: money(q.alreadyRefundedPaise, c) },
                { term: 'Still refundable', value: money(q.maxRefundPaise, c), strong: true },
                {
                  term: 'Payment',
                  value: q.paymentId ? `${q.paymentProvider ?? ''} ${q.paymentId}`.trim() : '-',
                },
              ]}
            />
          </div>

          {q.notes.length > 0 && (
            <ul className="flex list-disc flex-col gap-1 rounded-md bg-zinc-50 py-2 pr-3 pl-7 text-xs text-zinc-700">
              {q.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,220px)_1fr]">
            <UzNumberInput
              label={`Refund amount (${c})`}
              value={rupees}
              precision={2}
              min={0}
              inputMode="decimal"
              onValueChange={(v) => {
                setTouched(true);
                setRupees(v === null ? '' : v);
              }}
              error={touched || rupees !== '' ? amountError : undefined}
              hint={
                q.suggestedRefundPaise !== null
                  ? `Suggested: ${money(q.suggestedRefundPaise, c)}`
                  : 'No suggestion - decide from the figures above.'
              }
              disabled={!q.refundable}
            />
            <UzTextarea
              label="Internal note (optional)"
              placeholder="Why this amount - for the event log only."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={500}
            />
          </div>

          {onHold && (
            <UzAlert
              tone="warning"
              title="What the guest will see"
              description="An email saying the booking could not be confirmed and that the refund is on its way (5-7 working days). It never mentions the supplier or the reason."
            />
          )}
        </div>
      )}
    </UzDialog>
  );
};

/** A supplier figure, or why there is none yet. */
function supplierFigure(
  q: TravelBookingAdminRefundQuote,
  paise: string | null,
  currency: string,
): React.ReactNode {
  if (paise !== null) return money(paise, currency);
  if (q.cancellationStatus === 'PENDING') {
    return <span className="text-amber-700">awaiting supplier</span>;
  }
  if (q.status === 'ON_HOLD' || (q.status === 'BOOK_FAILED' && !q.isPartlyIssued)) {
    return <span className="text-zinc-500">none - nothing was booked</span>;
  }
  return <span className="text-zinc-400">-</span>;
}
